/**
 * Sección específica de destino para residuos
 * Muestra selector de tipo de destino (Bodega Transitoria o Disposición Final)
 * y campo condicional según la selección
 */

import React, { useEffect, useState, useRef } from 'react';
import { Card, CardBody, Col, Row, Alert } from 'reactstrap';
import OSelect from '../../../../../Components/OElements/OSelect';
import OInput from '../../../../../Components/OElements/OInput';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

// Helpers
import { getFinalDestinationList } from '../../../../../helpers/residue/backend_helper';
import {
	getNonHazardousTransitoryWarehouseList,
	getRespelWarehouseList,
} from '../../../../../helpers/warehouse/backend_helper';
import { useFormDictionary } from '../FormDictionaryContext';

const NP_WAREHOUSE_OPERATIONS = new Set([
	'no_peligrosos',
	'envio_bodega_no_peligrosos',
]);
const RESPEL_WAREHOUSE_OPERATIONS = new Set([
	'peligrosos',
	'envio_bodega_peligrosos',
]);

const needsWarehouseByEstablishment = operationType =>
	NP_WAREHOUSE_OPERATIONS.has(operationType) ||
	RESPEL_WAREHOUSE_OPERATIONS.has(operationType);

export const ResiduosDestinoSectionSpec = {
	getInitialValues: () => ({
		residue_type_id: '', // MOVIDO desde ItemsSection - igual que legacy
		destiny_type_id: '',
		establishment_destination_id: '',
		warehouse_id: '',
		final_destination_id: '',
		// Campos del receptor (para DTE)
		receptor_rut: '',
		receptor_name: '',
		receptor_establishment_address: '',
		receptor_establishment_commune: '',
		receptor_giro: '',
		receptor_phone: '', // Campo Contacto requerido por DTE
		siep_code_destination: '',
	}),
	getValidationSchema: () => ({
		// residue_type_id se setea automáticamente desde residuos_operation_type
		destiny_type_id: Yup.string().required('Tipo de destino es requerido'),
		establishment_destination_id: Yup.string().when(
			['destiny_type_id', 'residuos_operation_type'],
			{
				is: (destinyTypeId, operationType) =>
					destinyTypeId === '1' && needsWarehouseByEstablishment(operationType),
				then: schema => schema.required('Establecimiento de destino es requerido'),
				otherwise: schema => schema.notRequired(),
			},
		),
		warehouse_id: Yup.string().when('destiny_type_id', {
			is: '1',
			then: schema => schema.required('Bodega es requerida'),
			otherwise: schema => schema.notRequired(),
		}),
		final_destination_id: Yup.string().when('destiny_type_id', {
			is: '2',
			then: schema => schema.required('Destino final es requerido'),
			otherwise: schema => schema.notRequired(),
		}),
		// Campos del receptor
		receptor_rut: Yup.string().required('Requerido'),
		receptor_name: Yup.string().required('Requerido'),
		receptor_establishment_address: Yup.string().required('Requerido'),
		receptor_establishment_commune: Yup.string().required('Requerido'),
		receptor_phone: Yup.string().required('Teléfono del receptor es requerido'),
	}),
	toPayload: values => ({
		dtefacele: {
			Encabezado: {
			Receptor: {
				RUTRecep: values.receptor_rut || '',
				RznSocRecep: values.receptor_name || '',
				DirRecep: values.receptor_establishment_address || '',
				CmnaRecep: values.receptor_establishment_commune || '',
				CiudadRecep: values.receptor_establishment_commune || '',
				GiroRecep: values.receptor_giro || '',
				Contacto: values.receptor_phone || '',
			},
				Transporte: {
					DirDest: values.receptor_establishment_address || '',
					CmnaDest: values.receptor_establishment_commune || '',
					CiudadDest: values.receptor_establishment_commune || '',
				},
			},
			CamposLibres: {
				CodigoSiepDestino: values.siep_code_destination || '',
			},
		},
	}),
};

export const ResiduosDestinoSection = () => {
	const validation = useFormikContext();
	const { establishments, findEstablishmentById } = useFormDictionary();
	const destinyType = validation.values.destiny_type_id;
	const operationType = validation.values.residuos_operation_type || '';
	const destinationEstablishmentId =
		validation.values.establishment_destination_id || '';
	const [warehouseOptions, setWarehouseOptions] = useState([]);
	const [warehouseLoading, setWarehouseLoading] = useState(false);
	const [warehouseLoadError, setWarehouseLoadError] = useState(null);
	const [selectedWarehouse, setSelectedWarehouse] = useState(null);
	const [selectedFinalDestination, setSelectedFinalDestination] = useState(null);
	const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(true);
	const showDestinationEstablishment =
		destinyType === '1' && needsWarehouseByEstablishment(operationType);
	const establishmentOptions = establishments.map(item => ({
		value: item._id,
		label: item.name,
		item,
	}));
	// Ref para evitar loop: trackear el último warehouse procesado
	const lastProcessedWarehouseRef = useRef(null);
	const lastProcessedFinalDestRef = useRef(null);

	// Limpiar campos cuando cambia el tipo de destino
	useEffect(() => {
		if (destinyType === '1') {
			// Bodega transitoria seleccionada - limpiar destino final
			if (validation.values.final_destination_id !== '') {
				validation.setFieldValue('final_destination_id', '', false);
			}
			setSelectedFinalDestination(null);
			lastProcessedFinalDestRef.current = null;
		} else if (destinyType === '2') {
			// Destino final seleccionado - limpiar bodega
			if (validation.values.warehouse_id !== '') {
				validation.setFieldValue('warehouse_id', '', false);
			}
			setSelectedWarehouse(null);
			lastProcessedWarehouseRef.current = null;
		}
	}, [destinyType]);

	useEffect(() => {
		if (destinyType !== '1') {
			setWarehouseOptions([]);
			setWarehouseLoadError(null);
			return undefined;
		}
		const needsRespel = RESPEL_WAREHOUSE_OPERATIONS.has(operationType);
		const needsNp = NP_WAREHOUSE_OPERATIONS.has(operationType);
		if (!needsRespel && !needsNp) {
			setWarehouseOptions([]);
			return undefined;
		}
		if (!destinationEstablishmentId) {
			setWarehouseOptions([]);
			setWarehouseLoadError(
				'Seleccione el establecimiento de destino para listar bodegas.',
			);
			return undefined;
		}

		let cancelled = false;
		const loadWarehouses = async () => {
			setWarehouseLoading(true);
			setWarehouseLoadError(null);
			try {
				const list = needsRespel
					? await getRespelWarehouseList(destinationEstablishmentId)
					: await getNonHazardousTransitoryWarehouseList(
							destinationEstablishmentId,
						);
				if (cancelled) return;
				setWarehouseOptions(list);
				const currentId = validation.values.warehouse_id;
				if (
					currentId &&
					!list.some(w => String(w._id) === String(currentId))
				) {
					validation.setFieldValue('warehouse_id', '', false);
					setSelectedWarehouse(null);
					lastProcessedWarehouseRef.current = null;
				}
			} catch {
				if (!cancelled) {
					setWarehouseOptions([]);
					setWarehouseLoadError('No se pudieron cargar las bodegas.');
				}
			} finally {
				if (!cancelled) setWarehouseLoading(false);
			}
		};
		loadWarehouses();
		return () => {
			cancelled = true;
		};
	}, [destinyType, operationType, destinationEstablishmentId, validation.values.warehouse_id]);

	const handleDestinationEstablishmentSelected = establishment => {
		const nextId = establishment?._id || establishment?.value || '';
		if (validation.values.establishment_destination_id !== nextId) {
			validation.setFieldValue('establishment_destination_id', nextId, false);
			validation.setFieldValue('warehouse_id', '', false);
			setSelectedWarehouse(null);
			lastProcessedWarehouseRef.current = null;
		}
	};

	// Actualizar datos del receptor cuando se selecciona bodega transitoria
	// Usa ref para evitar re-ejecuciones innecesarias
	useEffect(() => {
		if (destinyType !== '1' || !selectedWarehouse) return;
		// Evitar reprocesar el mismo warehouse
		if (lastProcessedWarehouseRef.current === selectedWarehouse._id) return;
		lastProcessedWarehouseRef.current = selectedWarehouse._id;

		const warehouseEstablishment = findEstablishmentById(
			selectedWarehouse.establishment_id
		);
		
		if (warehouseEstablishment) {
			validation.setFieldValue(
				'receptor_rut',
				warehouseEstablishment.company?.rut || '',
				false
			);
			validation.setFieldValue(
				'receptor_name',
				`${warehouseEstablishment.company?.name || ''} - ${selectedWarehouse.name}`,
				false
			);
			validation.setFieldValue(
				'receptor_establishment_address',
				selectedWarehouse.address || warehouseEstablishment.address || '',
				false
			);
			validation.setFieldValue(
				'receptor_establishment_commune',
				warehouseEstablishment.commune?.name || '',
				false
			);
			validation.setFieldValue(
				'receptor_giro',
				warehouseEstablishment.company?.business_activity || 'Acuicultura',
				false
			);
			validation.setFieldValue(
				'receptor_phone',
				warehouseEstablishment.company?.phone || warehouseEstablishment.phone || '',
				false
			);
			validation.setFieldValue(
				'siep_code_destination',
				selectedWarehouse.code_sap || '',
				false
			);
		}
	}, [destinyType, selectedWarehouse, findEstablishmentById]);

	// Actualizar datos del receptor cuando se selecciona destino final
	useEffect(() => {
		if (destinyType !== '2' || !selectedFinalDestination) return;
		// Evitar reprocesar el mismo destino final
		if (lastProcessedFinalDestRef.current === selectedFinalDestination._id) return;
		lastProcessedFinalDestRef.current = selectedFinalDestination._id;

		validation.setFieldValue('receptor_rut', selectedFinalDestination.rut, false);
		validation.setFieldValue('receptor_name', selectedFinalDestination.name, false);
		validation.setFieldValue(
			'receptor_establishment_address',
			selectedFinalDestination.address,
			false
		);
		validation.setFieldValue(
			'receptor_establishment_commune',
			selectedFinalDestination.commune || '',
			false
		);
		validation.setFieldValue(
			'receptor_giro',
			selectedFinalDestination.business_activity || '',
			false
		);
		validation.setFieldValue(
			'siep_code_destination',
			selectedFinalDestination.code || '',
			false
		);
	}, [destinyType, selectedFinalDestination]);

	return (
		<Card className='border mb-0'>
			<CardBody className='p-4'>
				<div className='pb-2'>
					<h5>Información de destino</h5>
					<p className='text-muted mb-0'>
						Seleccione el tipo de destino de los residuos.
					</p>
				</div>
				<Row className='g-3'>
					<Col lg={showDestinationEstablishment ? 4 : 6}>
						<OSelect
							label='Tipo de Destino'
							id='destiny_type_id'
							name='destiny_type_id'
							isRequired={true}
							value={validation.values.destiny_type_id || ''}
							itemsSelect={[
								{
									value: '1',
									label: 'Bodega Transitoria',
									item: { _id: '1', name: 'Bodega Transitoria' },
								},
								{
									value: '2',
									label: 'Disposición Final',
									item: { _id: '2', name: 'Disposición Final' },
								},
							]}
							validation={validation}
						/>
					</Col>

					{showDestinationEstablishment && (
						<Col lg={4}>
							<OSelect
								key={`establishment-destination-${establishmentOptions.length}`}
								label='Establecimiento de Destino'
								id='establishment_destination_id'
								name='establishment_destination_id'
								isRequired={true}
								value={validation.values.establishment_destination_id || ''}
								itemsSelect={establishmentOptions}
								onSelect={handleDestinationEstablishmentSelected}
								validation={validation}
							/>
						</Col>
					)}

					<Col lg={showDestinationEstablishment ? 4 : 6}>
						{destinyType === '1' && warehouseLoadError && (
							<Alert color='warning' fade={false} className='mb-2 py-2'>
								{warehouseLoadError}
							</Alert>
						)}
						{destinyType === '1' &&
							!warehouseLoadError &&
							!warehouseLoading &&
							warehouseOptions.length === 0 &&
							(RESPEL_WAREHOUSE_OPERATIONS.has(operationType) ||
								NP_WAREHOUSE_OPERATIONS.has(operationType)) &&
							destinationEstablishmentId && (
								<Alert color='info' fade={false} className='mb-2 py-2'>
									No hay bodegas transitorias configuradas para este tipo de
									residuo en el establecimiento de destino.
								</Alert>
							)}
						{destinyType === '1' && (
							<OSelect
								key={`warehouse-destination-${operationType}`}
								label='Bodega Transitoria'
								id='warehouse_id'
								name='warehouse_id'
								isRequired={true}
								disabled={warehouseLoading || !!warehouseLoadError}
								value={validation.values.warehouse_id || ''}
								itemsSelect={warehouseOptions.map(wh => ({
									value: wh._id,
									label: `${wh.name || ''} ${
										wh.code_sap ? `(${wh.code_sap})` : ''
									}`,
									item: wh,
								}))}
								validation={validation}
								onSelect={warehouse => setSelectedWarehouse(warehouse)}
							/>
						)}

						{destinyType === '2' && (
							<OSelect
								key='final-destination'
								label='Destino Final'
								id='final_destination_id'
								name='final_destination_id'
								isRequired={true}
								value={validation.values.final_destination_id || ''}
								requestApi={() => getFinalDestinationList({})}
								validation={validation}
								onSelect={finalDest => setSelectedFinalDestination(finalDest)}
							/>
						)}

						{!destinyType && (
							<Alert color='info' fade={false} className='m-0 py-2'>
								<i className='ri-information-line me-1'></i>
								Seleccione primero el <strong>tipo de destino</strong> para
								poder elegir el destino específico
							</Alert>
						)}
					</Col>
					
					{/* Divider with centered toggle */}
					<Col lg={12} className='p-0'>
						<div className='divider-with-toggle'>
							<hr className='hr' />
							<button
								type='button'
								className='divider-toggle-btn'
								aria-expanded={!isDetailsCollapsed}
								onClick={() => setIsDetailsCollapsed(prev => !prev)}
							>
								<i className='ri-arrow-up-s-line'></i>
							</button>
						</div>
					</Col>
					
					{/* Campos de autocompletado del Receptor (se envían al API) */}
					<Col lg={12} className='p-0'>
						<div
							className={
								'collapsible-content' +
								(isDetailsCollapsed ? ' is-collapsed' : '')
							}
						>
							<Row className='g-3'>
								<Col lg={4}>
									<OInput
										id='receptor_rut'
										name='receptor_rut'
										type='text'
										label='Rut Titular Receptor'
										className='form-control'
										value={validation.values.receptor_rut || ''}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={8}>
									<OInput
										id='receptor_name'
										name='receptor_name'
										type='text'
										label='Razón Social Receptor'
										className='form-control'
										value={validation.values.receptor_name || ''}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={8}>
									<OInput
										id='receptor_establishment_address'
										name='receptor_establishment_address'
										label='Dirección Destino'
										type='text'
										className='form-control'
										value={
											validation.values.receptor_establishment_address || ''
										}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='receptor_establishment_commune'
										name='receptor_establishment_commune'
										label='Comuna Destino'
										type='text'
										className='form-control'
										value={
											validation.values.receptor_establishment_commune || ''
										}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='receptor_phone'
										name='receptor_phone'
										label='Teléfono Receptor'
										type='text'
										className='form-control'
										value={validation.values.receptor_phone || ''}
										validation={validation}
									/>
								</Col>
							</Row>
						</div>
					</Col>
				</Row>
			</CardBody>
		</Card>
	);
};
